import React, { type ReactNode } from "react";
import ReactSelect, { type MultiValue, type SingleValue } from "react-select";
import styles from "./Select.module.scss";
import withLabelAndError from "../withLabelAndError";
import { Controller } from "react-hook-form";
import { combineClasses } from "@/lib/utils";
import LoadingText from "@/components/LoadingText/LoadingText";

export interface SelectOption {
  value: string | number;
  label: ReactNode;
}

interface SelectProps {
  control?: any;
  validations?: any;
  name: string;
  placeholder?: string;
  isDisabled?: boolean;
  options?: SelectOption[];
  optionsHeight?: string | number;
  isMulti?: boolean;
  className?: string;
  loadingText?: string;
  isLoading?: boolean;
  value?: any;
  onChange?: (val: any) => void;
  onBlur?: () => void;
  //paginated select
  onMenuOpen?: () => void;
  onMenuScrollToBottom?: () => void;
}

const Select: React.FC<SelectProps> = (props) => {
  const {
    control,
    validations,
    name,
    placeholder,
    isDisabled,
    options,
    optionsHeight,
    isMulti,
    className = "",
    loadingText = "Loading",
    isLoading = false,
    ...rest
  } = props;

  const loadingOption: SelectOption = {
    label: <LoadingText text={loadingText} style={{ fontWeight: "400" }} />,
    value: "loading",
  };

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,

      borderRadius: "5px",
      padding: "0 4px",
      minHeight: "2.5rem",
      height: "2.5rem",
      boxShadow: "none",
      alignItems: "center",
      backgroundColor: state.isDisabled ? "#f3f4f6" : "#ffffff",
      // border: `1px solid ${state.isFocused ? "#9a9a9abb" : "#d3d3d3"}`,
      "&:hover": { borderColor: "#9a9a9abb" },
    }),
    valueContainer: (provided: any,state:any) => ({
      ...provided,
      padding: "0 8px", // reduce default padding
    }),
    // input: (provided: any) => ({
    //   ...provided,
    //   margin: "0",
    // // padding: "0 8px",
    // fontSize: "14px",
    // color: "#000",
    // // lineHeight: "1.2rem",
    // // visibility: "visible",
    // // alignItems: "flex-start",
    // }),
    placeholder: (provided: any, state: any) => ({
      ...provided,
      fontSize: "0.875rem",
      fontFamily: "'DM Sans', sans-serif",
      margin: "5",
      color: state.isDisabled ? "#9ca3af" : "#978d8d",
      // marginright:"2" ,
      boxsizing: "border-box",
      // marginbottom: "10",
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      cursor: "pointer",
      backgroundColor: state.isSelected
        ? "#2684ff"
        : state.isFocused
        ? "#f0f0f0"
        : "white",
      color: state.isSelected ? "white" : "black",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: "#666",
      padding: "4px",
      "&:hover": {
        color: "#000",
        //  paddingTop: "40px",
        alignItems: "center",
      },
    }),

    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999, // Ensure dropdown appears above MUI modal (z-index 1300)
    }),
  };

  const renderInput = (field: {
    value: any;
    onChange: (value: any) => void;
    onBlur: () => void;
    name: string;
  }) => (
    <ReactSelect
      {...rest}
      id={field.name}
      className={combineClasses(styles.inputField, className)}
      value={
        isLoading
          ? loadingOption
          : isMulti
          ? options?.filter((opt) => (field.value ?? []).includes(opt.value))
          : options?.find((opt) => opt.value === field.value) ?? null
      }
      options={isLoading ? [loadingOption] : options || []}
      styles={customStyles}
      placeholder={placeholder}
      classNamePrefix="react-select"
      isDisabled={isDisabled || isLoading}
      isMulti={isMulti}
      isSearchable={true}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      onChange={(selected) => {
        if (isMulti) {
          field.onChange(
            (selected as MultiValue<SelectOption>)?.map((s) => s.value) ?? []
          );
        } else {
          field.onChange(
            (selected as SingleValue<SelectOption>)?.value ?? null
          );
        }
      }}
      onBlur={field.onBlur}
      //paginated selet options
      onMenuOpen={props.onMenuOpen}
      onMenuScrollToBottom={props.onMenuScrollToBottom}
    />
  );

  if (control) {
    return (
      <Controller
        control={control}
        rules={validations}
        name={name}
        render={({ field }) => renderInput(field)}
      />
    );
  }

  return renderInput({
    value: rest?.value ?? null,
    onChange: rest?.onChange ?? (() => {}),
    onBlur: rest?.onBlur ?? (() => {}),
    name: name ?? "",
  });
};

const SelectWithLabelAndError = withLabelAndError(Select);
export default SelectWithLabelAndError;
export { Select };
